import { useEffect, useRef, useState } from "react"
import { Check, Copy } from "lucide-react"
import { useToast } from "@/hooks/use-toast"
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "@/components/ui/toast"

// Task #759 appends `\n\nError ID: <id>` to error toast descriptions via
// `parseErrorMessage`. Detect that suffix here so we can render the ID with
// an inline copy button (Task #773) for faster, typo-free support handoff.
const ERROR_ID_RE = /^([\s\S]*?)\n\nError ID:\s*(\S+)\s*$/

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // Fall through to the legacy fallback below.
  }
  // Legacy fallback for browsers / contexts without the async Clipboard API
  // (older Safari, insecure origins, etc.). Selecting a hidden textarea and
  // calling `execCommand("copy")` still works in those environments.
  if (typeof document === "undefined") return false
  try {
    const ta = document.createElement("textarea")
    ta.value = text
    ta.setAttribute("readonly", "")
    ta.style.position = "fixed"
    ta.style.top = "0"
    ta.style.left = "0"
    ta.style.opacity = "0"
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand("copy")
    document.body.removeChild(ta)
    return ok
  } catch {
    return false
  }
}

function CopyErrorIdButton({ errorId }: { errorId: string }) {
  const [copied, setCopied] = useState(false)
  const resetTimerRef = useRef<number | null>(null)
  useEffect(() => {
    return () => {
      if (resetTimerRef.current !== null) {
        window.clearTimeout(resetTimerRef.current)
      }
    }
  }, [])
  return (
    <button
      type="button"
      onClick={async (e) => {
        e.stopPropagation()
        const ok = await copyToClipboard(errorId)
        if (!ok) return
        setCopied(true)
        if (resetTimerRef.current !== null) {
          window.clearTimeout(resetTimerRef.current)
        }
        resetTimerRef.current = window.setTimeout(() => {
          setCopied(false)
          resetTimerRef.current = null
        }, 1500)
      }}
      className="ml-1 inline-flex items-center gap-1 rounded border border-current/30 px-1.5 py-0.5 text-xs font-medium opacity-80 hover-elevate active-elevate-2"
      aria-label={copied ? "Copied error ID" : "Copy error ID"}
      data-testid="button-copy-error-id"
    >
      {copied ? (
        <>
          <Check className="h-3 w-3" aria-hidden="true" />
          <span data-testid="text-copy-error-id-status">Copied</span>
        </>
      ) : (
        <>
          <Copy className="h-3 w-3" aria-hidden="true" />
          <span>Copy</span>
        </>
      )}
    </button>
  )
}

function renderDescription(description: React.ReactNode): React.ReactNode {
  if (typeof description !== "string") return description
  const match = description.match(ERROR_ID_RE)
  if (!match) return description
  const [, base, errorId] = match
  return (
    <>
      {base}
      {"\n\n"}
      <span className="inline-flex flex-wrap items-center gap-1">
        <span>
          Error ID: <span data-testid="text-error-id">{errorId}</span>
        </span>
        <CopyErrorIdButton errorId={errorId} />
      </span>
    </>
  )
}

export function Toaster() {
  const { toasts } = useToast()

  return (
    <ToastProvider>
      {toasts.map(function ({ id, title, description, action, ...props }) {
        return (
          <Toast key={id} {...props}>
            <div className="grid gap-1">
              {title && <ToastTitle>{title}</ToastTitle>}
              {description && (
                // Task #759: `whitespace-pre-line` so a multi-line description
                // (e.g. user-facing error followed by `\n\nError ID: <id>`
                // appended by `parseErrorMessage`) renders on separate lines
                // instead of collapsing into one run-on string.
                <ToastDescription className="whitespace-pre-line">
                  {renderDescription(description)}
                </ToastDescription>
              )}
            </div>
            {action}
            <ToastClose />
          </Toast>
        )
      })}
      <ToastViewport />
    </ToastProvider>
  )
}
